import {
  DynamicFormAttachmentsContextData,
  FormActionData,
  FormBuiltInActions,
  RenderForm,
  ToolbarAction,
} from "@entaconsulting/form";
import { StyledEngineProvider, createTheme } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { useResourcePath } from "common/hooks/useResourcePath";
import { memo, useCallback, useMemo } from "react";
import {
  addAttachments,
  cancelOperation,
  purgeOperations,
  removeAttachment,
  replaceAttachments,
  retryOperation,
  submitAll,
} from "store/attachments/attachmentsSlice";
import { saveSampleData } from "store/form/formDataSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import PreviewErrorHandler from "./previewErrorHandler";

type FormPreviewProps = {};
const FormPreview = memo((props: FormPreviewProps) => {
  const dispatch = useAppDispatch();

  const formDefinitionPreview = useAppSelector(
    (state) => state.developer.edit.editor.formDefinitionPreview
  );
  const { initialValues, isSubmitting } = useAppSelector(
    (state) => state.formData
  );

  const attachmentsState = useAppSelector((state) => state.attachments);
  const attachments: DynamicFormAttachmentsContextData = useMemo(
    () => ({
      ...attachmentsState,
      addAttachments: (fieldName, filesToAdd) =>
        dispatch(addAttachments(fieldName, filesToAdd)),
      replaceAttachments: (fieldName, filesToAdd) =>
        dispatch(replaceAttachments(fieldName, filesToAdd)),
      removeAttachment: (fieldName, fileName) =>
        dispatch(removeAttachment(fieldName, fileName)),
      retryOperation: (fieldName, fileName) =>
        dispatch(retryOperation(fieldName, fileName)),
      cancelOperation: (fieldName, fileName) =>
        dispatch(cancelOperation(fieldName, fileName)),
      purgeOperations: (fieldName, fileNames) =>
        dispatch(purgeOperations(fieldName, fileNames)),
      submitAll: (fieldName, operationFilter, uploadFiles) =>
        dispatch(submitAll(fieldName, operationFilter, uploadFiles)),
    }),
    [attachmentsState, dispatch]
  );
  const getResourcePath = useResourcePath();

  const lastSavedDate = useMemo(() => new Date(), []);

  const handleLoadSampleData = useCallback(
    (data: FormActionData, formBuiltInActions?: FormBuiltInActions) => {
      formBuiltInActions?.restart(initialValues);
    },
    [initialValues]
  );

  const handleValidate = useCallback(() => {}, []);

  const handleSaveSampleData = useCallback(
    (data: FormActionData) => {
      dispatch(saveSampleData(data.values));
    },
    [dispatch]
  );

  const handleClearSampleData = useCallback(
    (data: FormActionData, formBuiltInActions?: FormBuiltInActions) => {
      dispatch(saveSampleData({}));
    },
    [dispatch]
  );
  const toolbarActions: ToolbarAction[] = useMemo(() => {
    return [
      {
        key: "validate",
        label: "Validar",
        validationRequired: true,
        enabled: !isSubmitting,
        onAction: handleValidate,
      },
      {
        key: "loadSampleData",
        label: "Cargar datos de ejemplo",
        validationRequired: false,
        enabled: !isSubmitting,
        onAction: handleLoadSampleData,
      },
      {
        key: "saveSampleData",
        label: "Guardar datos de ejemplo",
        validationRequired: false,
        enabled: !isSubmitting,
        onAction: handleSaveSampleData,
      },
      {
        key: "clearSampleData",
        label: "Borrar datos de ejemplo",
        validationRequired: false,
        enabled: !isSubmitting,
        onAction: handleClearSampleData,
      },
    ];
  }, [
    isSubmitting,
    handleLoadSampleData,
    handleValidate,
    handleSaveSampleData,
    handleClearSampleData,
  ]);

  const theme = useTheme();
  const formTheme = useMemo(
    () =>
      createTheme({
        ...theme,
        components: {
          MuiTextField: {
            defaultProps: {
              variant: "standard",
            },
          },
        },
      }),
    [theme]
  );

  return (
    <PreviewErrorHandler resetObject={formDefinitionPreview}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={formTheme}>
          <RenderForm
            formDefinition={formDefinitionPreview}
            initialValues={initialValues}
            isSubmitting={isSubmitting}
            attachments={attachments}
            status="Preview"
            lastSaved={lastSavedDate}
            allowEdit={!isSubmitting}
            getResourcePath={getResourcePath}
            toolbarActions={toolbarActions}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    </PreviewErrorHandler>
  );
});

FormPreview.displayName = "FormPreview";

export default FormPreview;
